import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLocalization } from '../../hooks/useLocalization';
import { SettingsPanel } from '../SettingsPanel';
import { AdminAIBuilder } from './AdminAIBuilder';
import { RoomMappingPanel } from './RoomMappingPanel';
import HotelManager from './HotelManager';
import StaticHotelManager from './modules/hotels/StaticHotelManager';
import ScrapedHotelsManager from './modules/hotels/ScrapedHotelsManager';
// import MediaLibrary from './MediaLibrary';

import TourCacheManager from './TourCacheManager';
import DataExplorer from './DataExplorer';
import FieldMapper from './FieldMapper';
import DestinationManager from './DestinationManager';
import DestinationsContent from './modules/DestinationsContent';
import BookingsManager from './BookingsManager';
import EmailSettingsManager from './EmailSettingsManager';
import UserManagement from './modules/users/UserManagement';
// First test batch - simple modules
import SEOAnalyzer from './modules/seo/SEOAnalyzer';
import SupportDashboard from './modules/support/SupportDashboard';
import LumiReportingContainer from './modules/reporting/LumiReportingContainer';
import AdManagementContent from './modules/AdManagementContent';
import SocialMediaContent from './modules/SocialMediaContent';
import OtherServicesContent from './modules/OtherServicesContent';
// Lazy loaded management modules
const AIManager = React.lazy(() => import('./modules/ai/AIManager'));
const SiteManagement = React.lazy(() => import('./modules/system/SiteManagement'));
const CacheManagement = React.lazy(() => import('./modules/system/CacheManagement'));
const ApiKeysSettings = React.lazy(() => import('./modules/system/ApiKeysSettings'));
const APIDataMapper = React.lazy(() => import('./modules/system/APIDataMapper'));
const APIKeyManagement = React.lazy(() => import('./modules/system/APIKeyManagement'));
const ApiDiagnostics = React.lazy(() => import('./modules/system/ApiDiagnostics'));
const MediaContent = React.lazy(() => import('./modules/MediaContent'));
const UnifiedAPIHub = React.lazy(() => import('./modules/system/UnifiedAPIHub'));
const TourAssistAdmin = React.lazy(() => import('./modules/TourAssistAdmin'));
const AIPlanner = React.lazy(() => import('./modules/ai/AIPlanner'));
const ReisegigantenExport = React.lazy(() => import('./modules/reisegiganten/ReisegigantenExport'));
import { AdminThemeProvider, useAdminTheme, ThemeSelector } from '../../contexts/AdminThemeContext';

import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../auth/AuthModal';
import { ApiLogEntry, Language } from '../../types';

// Loading fallback component
const ModuleLoadingFallback = ({ title }) => (
    <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading {title}...</p>
        </div>
    </div>
);



const StatsCard = ({ title, value, change, icon, iconBg, iconColor }) => (
    <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col transition-transform hover:-translate-y-1">
        <div className="flex justify-between items-start mb-2">
            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{title}</span>
            <div className={`w-8 h-8 rounded-lg ${iconBg} ${iconColor} flex items-center justify-center`}>
                <span className="material-icons-round text-sm">{icon}</span>
            </div>
        </div>
        <div className="flex items-baseline gap-2 mt-auto">
            <span className="text-2xl font-bold text-gray-800 dark:text-white">{value}</span>
            {change && (
                <span className={`text-xs font-medium flex items-center px-1.5 py-0.5 rounded ${change.startsWith('+') ? 'text-green-500 bg-green-50 dark:bg-green-900/20' : 'text-gray-400'
                    }`}>
                    {change}
                </span>
            )}
        </div>
    </div>
);

const ModuleCard = ({ title, subtitle, description, icon, iconBg, iconColor, onClick, isHighlighted }) => (
    <div
        onClick={onClick}
        className={`${isHighlighted
            ? 'bg-gradient-to-br from-teal-600 via-primary to-emerald-600 text-white shadow-xl shadow-primary/40 border-2 border-teal-400/30'
            : 'bg-surface-light dark:bg-surface-dark border border-gray-100 dark:border-gray-700 hover:border-primary/30 dark:hover:border-primary/30'
            } p-6 rounded-2xl shadow-sm hover:shadow-lg transition-all group cursor-pointer relative overflow-hidden transform hover:scale-[1.02]`}
    >
        {!isHighlighted && (
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity transform group-hover:scale-110 duration-500">
                <span className="material-icons-round text-8xl text-primary">{icon}</span>
            </div>
        )}
        {isHighlighted && (
            <>
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/20 via-transparent to-emerald-500/20"></div>
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/20 rounded-full blur-2xl group-hover:bg-white/30 transition-all duration-500"></div>
                <div className="absolute bottom-0 right-0 p-4 opacity-30">
                    <span className="material-icons-round text-8xl text-white">{icon}</span>
                </div>
            </>
        )}
        <div className={`w-12 h-12 rounded-xl ${isHighlighted ? 'bg-white/30 backdrop-blur-sm text-white border border-white/40 shadow-lg' : iconBg + ' ' + iconColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
            <span className="material-icons-round text-2xl">{icon}</span>
        </div>
        <h4 className={`text-lg font-bold ${isHighlighted ? 'text-white drop-shadow-md' : 'text-gray-800 dark:text-white group-hover:text-primary'} transition-colors relative z-10`}>{title}</h4>
        <p className={`text-xs font-medium mt-1 uppercase tracking-wide ${isHighlighted ? 'text-white/90' : 'text-gray-400'} relative z-10`}>{subtitle}</p>
        <p className={`text-sm mt-3 line-clamp-2 relative z-10 ${isHighlighted ? 'text-white/95' : 'text-gray-500 dark:text-gray-400'}`}>{description}</p>
        {isHighlighted && (
            <button className="mt-4 px-4 py-2 bg-white text-primary text-xs font-bold rounded-lg shadow-md hover:bg-gray-50 hover:shadow-lg transition-all relative z-10 flex items-center gap-2">
                <span className="material-icons-round text-sm">auto_awesome</span>
                Launch AI
            </button>
        )}
    </div>
);

const Sidebar = ({ activeTab, setActiveTab, language, setLanguage, t, isMobileOpen = false, onCloseMobile }) => {
    const { user } = useAuth();

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        if (onCloseMobile) {
            onCloseMobile();
        }
    };
    const menuItems = [
        { id: 'dashboard', icon: 'dashboard', labelKey: 'dashboard' },
        { id: 'petra-ai', icon: 'auto_awesome', labelKey: 'petra_ai', isPetra: true },
        { id: 'hotels', icon: 'apartment', labelKey: 'hotels' },
        { id: 'bookings', icon: 'book_online', labelKey: 'bookings' },
        { id: 'destinations', icon: 'travel_explore', labelKey: 'destinations' },
        { id: 'tours', icon: 'luggage', labelKey: 'tours_cache' },
        { id: 'flights', icon: 'flight', labelKey: 'flights' },
        { id: 'media', icon: 'perm_media', labelKey: 'media_library' },
        { id: 'reporting', icon: 'insights', labelKey: 'reporting' },
        { id: 'ads', icon: 'campaign', labelKey: 'ads_management' },
        { id: 'social', icon: 'share', labelKey: 'social_media' },
        { id: 'seo', icon: 'search', labelKey: 'seo_tools' },
        { id: 'support', icon: 'support_agent', labelKey: 'support_center' },
        { id: 'reisegiganten', icon: 'public', labelKey: 'reisegiganten_export' },
    ];

    const systemItems = [
        { id: 'users', icon: 'people', labelKey: 'users' },
        { id: 'site', icon: 'language', labelKey: 'contents' },
        { id: 'settings', icon: 'settings', labelKey: 'settings_menu' },
    ];

    const apiItems = [
        { id: 'api-hub', icon: 'hub', labelKey: 'api_management' },
        { id: 'data', icon: 'analytics', labelKey: 'data_explorer_menu' },
        { id: 'ai', icon: 'auto_awesome', labelKey: 'ai_manager' },
    ];


    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
                    onClick={onCloseMobile}
                />
            )}
            <aside className={`w-72 bg-surface-dark flex-shrink-0 flex flex-col transition-all duration-300 border-r border-gray-700 z-50
                fixed md:relative inset-y-0 left-0
                ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
            `}>
                {/* Logo */}
                <div className="h-24 flex items-center px-8 border-b border-gray-700/50">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary/20 text-primary rounded-xl flex items-center justify-center font-bold text-2xl shadow-inner shadow-primary/10">
                            L
                        </div>
                        <div>
                            <h1 className="text-white font-bold text-xl tracking-wide">LUMI<span className="text-primary">TOURS</span></h1>
                            <p className="text-[10px] text-gray-400 tracking-[0.2em] uppercase">{t('admin_panel')}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-8 px-5 space-y-2">
                    {menuItems.map(item => {
                        // Special Petra AI button rendering
                        if ((item as any).isPetra) {
                            const isActive = activeTab === 'petra-ai' || activeTab === 'ai-planner' || activeTab === 'tour-assist';
                            return (
                                <a
                                    key={item.id}
                                    onClick={() => handleTabClick('petra-ai')}
                                    className={`relative flex items-center gap-4 px-4 py-3.5 rounded-xl transition-all group cursor-pointer overflow-hidden
                                        ${isActive
                                            ? 'bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-500 text-white shadow-lg shadow-purple-500/30 scale-[1.02]'
                                            : 'bg-gradient-to-r from-rose-500/10 via-purple-500/10 to-indigo-500/10 text-purple-300 hover:from-rose-500/20 hover:via-purple-500/20 hover:to-indigo-500/20 hover:text-white hover:shadow-lg hover:shadow-purple-500/20'
                                        }`}
                                >
                                    {/* Animated glow ring */}
                                    <span className={`absolute inset-0 rounded-xl border border-purple-400/30 ${!isActive ? 'group-hover:animate-pulse' : ''}`} />
                                    {/* Sparkle shimmer overlay */}
                                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent group-hover:via-white/10 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                                    <span className={`material-icons-round text-xl ${isActive ? 'animate-pulse' : 'group-hover:animate-spin'} transition-all relative z-10`}>auto_awesome</span>
                                    <span className="font-bold text-sm tracking-wide relative z-10">Petra AI</span>
                                    {!isActive && <span className="ml-auto text-[9px] font-bold uppercase tracking-widest opacity-50 relative z-10">✦</span>}
                                </a>
                            );
                        }
                        // Normal menu items
                        return (
                            <a
                                key={item.id}
                                onClick={() => handleTabClick(item.id)}
                                className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all group cursor-pointer ${activeTab === item.id
                                    ? 'bg-gradient-to-r from-primary to-teal-600 text-white shadow-lg shadow-primary/20 hover:scale-[1.02]'
                                    : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                                    }`}
                            >
                                <span className={`material-icons-round text-xl ${activeTab !== item.id && 'group-hover:text-primary'} transition-colors`}>{item.icon}</span>
                                <span className={`${activeTab === item.id ? 'font-bold' : 'font-medium'} text-sm`}>{t(item.labelKey)}</span>
                            </a>
                        );
                    })}

                    <div className="pt-6 pb-2">
                        <p className="px-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                            {language === 'sv' ? 'System' : 'System'}
                        </p>
                    </div>

                    {systemItems.map(item => (
                        <a
                            key={item.id}
                            onClick={() => handleTabClick(item.id)}
                            className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all group cursor-pointer ${activeTab === item.id
                                ? 'bg-gradient-to-r from-primary to-teal-600 text-white shadow-lg shadow-primary/20'
                                : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                                }`}
                        >
                            <span className={`material-icons-round text-xl ${activeTab !== item.id && 'group-hover:text-primary'} transition-colors`}>{item.icon}</span>
                            <span className={`${activeTab === item.id ? 'font-bold' : 'font-medium'} text-sm`}>{t(item.labelKey)}</span>
                        </a>
                    ))}

                    <div className="pt-6 pb-2">
                        <p className="px-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                            {language === 'sv' ? 'API & Data' : 'API & Data'}
                        </p>
                    </div>

                    {apiItems.map(item => (
                        <a
                            key={item.id}
                            onClick={() => handleTabClick(item.id)}
                            className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all group cursor-pointer ${activeTab === item.id
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/20'
                                : 'text-gray-400 hover:bg-gray-800/50 hover:text-white'
                                }`}
                        >
                            <span className={`material-icons-round text-xl ${activeTab !== item.id && 'group-hover:text-blue-400'} transition-colors`}>{item.icon}</span>
                            <span className={`${activeTab === item.id ? 'font-bold' : 'font-medium'} text-sm`}>{t(item.labelKey)}</span>
                        </a>
                    ))}
                </nav>

                {/* Language Selector */}
                <div className="px-5 pb-4">
                    <div className="p-1 rounded-xl bg-gray-800/50 border border-gray-700 flex">
                        <button
                            onClick={() => setLanguage('en')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all ${language === 'en'
                                ? 'bg-primary text-white shadow-lg'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <span>🇺🇸</span> EN
                        </button>
                        <button
                            onClick={() => setLanguage('sv')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-bold transition-all ${language === 'sv'
                                ? 'bg-primary text-white shadow-lg'
                                : 'text-gray-400 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <span>🇸🇪</span> SV
                        </button>
                    </div>
                </div>

                {/* User Info */}
                <div className="p-6 border-t border-gray-700/50">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-800/50 border border-gray-700">
                        <img alt={user?.name || "Admin User"} className="w-10 h-10 rounded-full border-2 border-primary object-cover" src={`https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Admin')}&background=0D9488&color=fff`} />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-white truncate">{user?.name || 'Admin User'}</p>
                            <p className="text-xs text-gray-400 truncate">{user?.email || 'admin@lumitours.com'}</p>
                        </div>
                        <button className="text-gray-400 hover:text-white hover:bg-gray-700 p-1.5 rounded-lg transition-colors" title={t('logout')}>
                            <span className="material-icons-round text-xl">logout</span>
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

// Main Dashboard Content
const DashboardContent = ({ setActiveTab, version, setIsMobileSidebarOpen }) => {
    const { t, language } = useLocalization();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        bookings: { today: 0, change: '' },
        destinations: { total: 0, newThisWeek: 0 },
        media: { total: 0, recent: 0 },
        apiHealth: { status: 'checking', uptime: '...' }
    });
    const [trafficStats, setTrafficStats] = useState({
        activeUsers: 0,
        pageViews: 0,
        sessions: 0,
        bounceRate: 0,
        sources: [] as { source: string; percentage: number }[]
    });
    const [activities, setActivities] = useState<Array<{
        type: string;
        icon: string;
        title: string;
        subtitle: string;
        time: string;
        color: string;
    }>>([]);

    // Localization strings
    const labels = {
        en: {
            welcomeBack: 'Welcome Back',
            adminTitle: 'Admin Dashboard',
            adminSubtitle: 'Manage your platform from one central place.',
            recentBookings: 'Recent Bookings',
            today: 'Today',
            destinations: 'Destinations',
            newThisWeek: 'new this week',
            mediaFiles: 'Media Files',
            recentUploads: 'recent uploads',
            apiStatus: 'API Status',
            online: 'Online',
            offline: 'Offline',
            checking: 'Checking...',
            recentActivity: 'Recent Activity',
            viewAll: 'View All',
            noActivity: 'No recent activity',
            quickActions: 'Quick Actions',
            addDestination: 'Add Destination',
            uploadMedia: 'Upload Media',
            viewBookings: 'View Bookings',
            manageHotels: 'Manage Hotels',
            liveTraffic: 'Live Traffic',
            activeNow: 'Active Now',
            pageViews: 'Page Views',
            sessions: 'Sessions',
            bounceRate: 'Bounce Rate'
        },
        sv: {
            welcomeBack: 'Välkommen Tillbaka',
            adminTitle: 'Admin Panel',
            adminSubtitle: 'Hantera din plattform från en central plats.',
            recentBookings: 'Senaste Bokningar',
            today: 'Idag',
            destinations: 'Destinationer',
            newThisWeek: 'nya denna vecka',
            mediaFiles: 'Mediefiler',
            recentUploads: 'senaste uppladdningar',
            apiStatus: 'API Status',
            online: 'Online',
            offline: 'Offline',
            checking: 'Kontrollerar...',
            recentActivity: 'Senaste Aktivitet',
            viewAll: 'Visa Alla',
            noActivity: 'Ingen ny aktivitet',
            quickActions: 'Snabbåtgärder',
            addDestination: 'Lägg till Destination',
            uploadMedia: 'Ladda upp Media',
            viewBookings: 'Visa Bokningar',
            manageHotels: 'Hantera Hotell',
            liveTraffic: 'Live Trafik',
            activeNow: 'Aktiva Nu',
            pageViews: 'Sidvisningar',
            sessions: 'Sessioner',
            bounceRate: 'Avvisningsfrekvens'
        }
    };
    const l = labels[language] || labels.en;

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [bookingsRes, destinationsRes, mediaRes, healthRes, analyticsRes] = await Promise.all([
                    fetch('/api/bookings?limit=10').catch(() => null),
                    fetch('/api/destinations').catch(() => null),
                    fetch('/api/dam/stats').catch(() => null),
                    fetch('/api/system/health').catch(() => null),
                    fetch('/api/analytics/dashboard?period=week').catch(() => null)
                ]);

                const bookingsData = bookingsRes?.ok ? await bookingsRes.json() : null;
                const destinationsData = destinationsRes?.ok ? await destinationsRes.json() : null;
                const mediaData = mediaRes?.ok ? await mediaRes.json() : null;
                const healthData = healthRes?.ok ? await healthRes.json() : null;
                const analyticsData = analyticsRes?.ok ? await analyticsRes.json() : null;

                // Update traffic stats
                if (analyticsData?.success) {
                    setTrafficStats({
                        activeUsers: analyticsData.realtime?.activeUsers || 0,
                        pageViews: analyticsData.traffic?.totals?.pageViews || 0,
                        sessions: analyticsData.traffic?.totals?.sessions || 0,
                        bounceRate: analyticsData.traffic?.totals?.bounceRate || 0,
                        sources: analyticsData.sources?.sources || []
                    });
                }

                // Update stats
                const newStats = { ...stats };

                if (bookingsData?.success) {
                    const todayCount = bookingsData.today || bookingsData.bookings?.length || 0;
                    newStats.bookings = { today: todayCount, change: bookingsData.change || '' };
                }

                if (destinationsData?.success || Array.isArray(destinationsData?.destinations)) {
                    const destinations = destinationsData?.destinations || [];
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    const newThisWeek = destinations.filter((d: any) =>
                        d.createdAt && new Date(d.createdAt) > oneWeekAgo
                    ).length;
                    newStats.destinations = { total: destinations.length, newThisWeek };
                }

                if (mediaData?.success) {
                    newStats.media = {
                        total: mediaData.totalFiles || 0,
                        recent: mediaData.recentUploads || 0
                    };
                }

                newStats.apiHealth = {
                    status: healthData?.status === 'ok' || healthRes?.ok ? 'online' : 'offline',
                    uptime: healthData?.uptime || '99.9%'
                };

                setStats(newStats);

                // Build activity feed
                const newActivities: typeof activities = [];

                // Add recent bookings to activity
                if (bookingsData?.bookings) {
                    bookingsData.bookings.slice(0, 3).forEach((b: any) => {
                        newActivities.push({
                            type: 'booking',
                            icon: 'book_online',
                            title: language === 'sv' ? 'Ny bokning' : 'New Booking',
                            subtitle: `#${b.bookingNumber || b._id?.slice(-6)} - ${b.totalPrice || 0} ${b.currency || 'EUR'}`,
                            time: formatTimeAgo(b.createdAt, language),
                            color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30'
                        });
                    });
                }

                // Add recent destinations
                if (destinationsData?.destinations) {
                    destinationsData.destinations.slice(0, 2).forEach((d: any) => {
                        if (d.createdAt) {
                            newActivities.push({
                                type: 'destination',
                                icon: 'travel_explore',
                                title: language === 'sv' ? 'Destination tillagd' : 'Destination Added',
                                subtitle: d.name || d.title || 'Unknown',
                                time: formatTimeAgo(d.createdAt, language),
                                color: 'text-green-500 bg-green-100 dark:bg-green-900/30'
                            });
                        }
                    });
                }

                // Sort by recency and limit
                newActivities.sort((a, b) => {
                    const timeA = a.time.includes('min') ? 1 : a.time.includes('hour') ? 2 : 3;
                    const timeB = b.time.includes('min') ? 1 : b.time.includes('hour') ? 2 : 3;
                    return timeA - timeB;
                });
                setActivities(newActivities.slice(0, 5));

            } catch (error) {
                console.error('Failed to fetch dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
        return () => clearInterval(interval);
    }, [language]);

    // Helper function
    const formatTimeAgo = (dateStr: string, lang: string) => {
        if (!dateStr) return lang === 'sv' ? 'nyligen' : 'recently';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return lang === 'sv' ? 'just nu' : 'just now';
        if (diffMins < 60) return `${diffMins} ${lang === 'sv' ? 'min sedan' : 'min ago'}`;
        if (diffHours < 24) return `${diffHours}${lang === 'sv' ? 't sedan' : 'h ago'}`;
        return `${diffDays}${lang === 'sv' ? 'd sedan' : 'd ago'}`;
    };

    return (
        <div className="flex-1 overflow-y-auto">

            {/* Compact Hero */}
            <div className="relative h-[200px] md:h-[240px] w-full overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: 'url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920)',
                        backgroundAttachment: 'fixed'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-50/95 via-gray-50/30 to-gray-900/50 dark:from-gray-900/98 dark:via-gray-900/50 dark:to-gray-900/70" />
                <div className="relative z-10 text-center px-4 pt-10 md:pt-14">
                    <span className="inline-block py-1 px-3 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-[10px] font-bold uppercase tracking-wider mb-3 shadow-lg">
                        {l.welcomeBack}
                    </span>
                    <h1 className="text-2xl md:text-4xl font-bold text-white mb-1 drop-shadow-lg">
                        Lumi Tours <span className="text-amber-400">{l.adminTitle}</span>
                    </h1>
                    <p className="text-sm text-white/80 font-light max-w-md mx-auto drop-shadow-md">
                        {l.adminSubtitle}
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="px-4 md:px-6 lg:px-10 -mt-8 relative z-20 pb-8 max-w-7xl mx-auto">
                {/* Stats Grid - 2x2 on mobile, 4 columns on desktop */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
                    {/* Recent Bookings */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[9px] md:text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.recentBookings}</span>
                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                <span className="material-icons-round text-sm">book_online</span>
                            </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
                                {loading ? '...' : stats.bookings.today}
                            </span>
                            <span className="text-[10px] text-gray-400">{l.today}</span>
                        </div>
                    </div>

                    {/* Destinations */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[9px] md:text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.destinations}</span>
                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center">
                                <span className="material-icons-round text-sm">travel_explore</span>
                            </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
                                {loading ? '...' : stats.destinations.total}
                            </span>
                            {stats.destinations.newThisWeek > 0 && (
                                <span className="text-[10px] text-green-500 bg-green-50 dark:bg-green-900/20 px-1.5 py-0.5 rounded">
                                    +{stats.destinations.newThisWeek} {l.newThisWeek}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Media Files */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[9px] md:text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.mediaFiles}</span>
                            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                                <span className="material-icons-round text-sm">perm_media</span>
                            </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white">
                                {loading ? '...' : stats.media.total}
                            </span>
                            {stats.media.recent > 0 && (
                                <span className="text-[10px] text-purple-500">{stats.media.recent} {l.recentUploads}</span>
                            )}
                        </div>
                    </div>

                    {/* API Status */}
                    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all">
                        <div className="flex justify-between items-start mb-2">
                            <span className="text-[9px] md:text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{l.apiStatus}</span>
                            <div className={`w-7 h-7 md:w-8 md:h-8 rounded-lg flex items-center justify-center ${stats.apiHealth.status === 'online'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                : stats.apiHealth.status === 'offline'
                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                                }`}>
                                <span className="material-icons-round text-sm">
                                    {stats.apiHealth.status === 'online' ? 'check_circle' : stats.apiHealth.status === 'offline' ? 'error' : 'sync'}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className={`text-xl md:text-2xl font-bold ${stats.apiHealth.status === 'online' ? 'text-green-600 dark:text-green-400' :
                                stats.apiHealth.status === 'offline' ? 'text-red-600 dark:text-red-400' :
                                    'text-gray-500'
                                }`}>
                                {stats.apiHealth.status === 'online' ? l.online :
                                    stats.apiHealth.status === 'offline' ? l.offline : l.checking}
                            </span>
                            <span className="text-[10px] text-gray-400">{stats.apiHealth.uptime}</span>
                        </div>
                    </div>
                </div>

                {/* Live Traffic Stats */}
                <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-4 md:p-5 rounded-2xl shadow-lg mb-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <span className="material-icons-round text-white text-lg animate-pulse">sensors</span>
                            <span className="text-white font-bold text-sm">{l.liveTraffic}</span>
                            <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                        </div>
                        <button onClick={() => setActiveTab('reporting')} className="text-white/70 hover:text-white text-xs flex items-center gap-1">
                            <span>Details</span>
                            <span className="material-icons-round text-sm">arrow_forward</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                            <p className="text-white/70 text-[10px] uppercase tracking-wider mb-1">{l.activeNow}</p>
                            <p className="text-white text-2xl font-bold">{trafficStats.activeUsers}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                            <p className="text-white/70 text-[10px] uppercase tracking-wider mb-1">{l.pageViews}</p>
                            <p className="text-white text-2xl font-bold">{(trafficStats.pageViews / 1000).toFixed(1)}k</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                            <p className="text-white/70 text-[10px] uppercase tracking-wider mb-1">{l.sessions}</p>
                            <p className="text-white text-2xl font-bold">{trafficStats.sessions}</p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                            <p className="text-white/70 text-[10px] uppercase tracking-wider mb-1">{l.bounceRate}</p>
                            <p className="text-white text-2xl font-bold">{trafficStats.bounceRate?.toFixed(1) || 0}%</p>
                        </div>
                    </div>
                    {trafficStats.sources.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {trafficStats.sources.slice(0, 5).map((src, idx) => (
                                <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-2">
                                    <span className="text-white text-xs font-medium">{src.source}</span>
                                    <span className="text-white/60 text-[10px]">{src.percentage?.toFixed(0) || 0}%</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Two Column Layout: Activity Feed + Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                    {/* Activity Feed - Takes 2 columns */}
                    <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <span className="material-icons-round text-primary text-lg">timeline</span>
                                {l.recentActivity}
                            </h3>
                            <button onClick={() => setActiveTab('bookings')} className="text-xs font-medium text-primary hover:text-teal-600">
                                {l.viewAll}
                            </button>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {loading ? (
                                <div className="p-6 text-center text-gray-400">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
                                </div>
                            ) : activities.length === 0 ? (
                                <div className="p-6 text-center text-gray-400 text-sm">{l.noActivity}</div>
                            ) : (
                                activities.map((activity, i) => (
                                    <div key={i} className="p-3 md:p-4 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${activity.color}`}>
                                            <span className="material-icons-round text-base">{activity.icon}</span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{activity.title}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{activity.subtitle}</p>
                                        </div>
                                        <span className="text-[10px] text-gray-400 flex-shrink-0">{activity.time}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                            <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <span className="material-icons-round text-amber-500 text-lg">flash_on</span>
                                {l.quickActions}
                            </h3>
                        </div>
                        <div className="p-3 space-y-2">
                            <button
                                onClick={() => setActiveTab('destinations')}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors group"
                            >
                                <span className="material-icons-round text-green-600 dark:text-green-400">add_location</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-green-700 dark:group-hover:text-green-300">{l.addDestination}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('media')}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors group"
                            >
                                <span className="material-icons-round text-purple-600 dark:text-purple-400">cloud_upload</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-purple-700 dark:group-hover:text-purple-300">{l.uploadMedia}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('bookings')}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
                            >
                                <span className="material-icons-round text-blue-600 dark:text-blue-400">list_alt</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-300">{l.viewBookings}</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('hotels')}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors group"
                            >
                                <span className="material-icons-round text-amber-600 dark:text-amber-400">apartment</span>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-amber-700 dark:group-hover:text-amber-300">{l.manageHotels}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <footer className="mt-8 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center text-xs text-gray-400">
                    <p>© 2026 Lumi Tours Admin System {version || ''}</p>
                    <div className="flex gap-4 mt-2 md:mt-0">
                        <a className="hover:text-primary transition-colors" href="#">Documentation</a>
                        <a className="hover:text-primary transition-colors" href="#">Support</a>
                    </div>
                </footer>
            </div>
        </div>
    );
};



// User Management Content
const UsersContent = () => {
    const users = [
        { name: 'Elsa Andersson', email: 'elsa@lumitours.se', role: 'Guide', location: 'Kiruna, SE', status: 'Active', lastActive: '2 mins ago' },
        { name: 'Lars Jensen', email: 'lars.j@lumitours.se', role: 'Admin', location: 'Stockholm, SE', status: 'Active', lastActive: '4 hours ago' },
        { name: 'Astrid Lindberg', email: 'astrid.l@gmail.com', role: 'Customer', location: 'Gothenburg, SE', status: 'Offline', lastActive: '2 days ago' },
        { name: 'Erik Svensson', email: 'e.svensson@lumitours.se', role: 'Guide', location: 'Abisko, SE', status: 'On Leave', lastActive: '1 week ago' },
    ];

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
            <div className="max-w-[1200px] mx-auto flex flex-col gap-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h1 className="text-3xl md:text-4xl font-black tracking-tight text-gray-800 dark:text-white">
                            User Management
                            <span className="block text-xl md:text-2xl font-bold text-gray-500 dark:text-gray-400 mt-1">Användarhantering</span>
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 mt-1">Manage access, roles, and profiles for staff and customers.</p>
                    </div>
                    <button className="flex items-center justify-center gap-2 bg-primary hover:bg-teal-600 text-white font-bold py-2.5 px-5 rounded-lg shadow-lg shadow-primary/25 transition-all active:scale-95">
                        <span className="material-icons-round">add</span>
                        <span className="whitespace-nowrap">Add User / Lägg till</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="glass-card p-4 rounded-xl flex flex-col md:flex-row gap-4 justify-between items-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium hover:border-primary/50 transition-colors">
                            <span className="material-icons-round text-[20px]">filter_list</span>
                            <span>Filter / Filtrera</span>
                        </button>
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                        <span className="text-gray-500 text-sm mr-2">View:</span>
                        <button className="p-2 text-gray-600 dark:text-white bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-primary transition-colors">
                            <span className="material-icons-round">grid_view</span>
                        </button>
                        <button className="p-2 text-white bg-primary rounded-lg border border-primary shadow-md">
                            <span className="material-icons-round">view_list</span>
                        </button>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">
                                    <th className="p-4 pl-6">User / Användare</th>
                                    <th className="p-4">Role / Roll</th>
                                    <th className="p-4">Location / Plats</th>
                                    <th className="p-4">Status</th>
                                    <th className="p-4">Last Active</th>
                                    <th className="p-4 text-right pr-6">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {users.map((user, i) => (
                                    <tr key={i} className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="p-4 pl-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                                                    {user.name.split(' ').map(n => n[0]).join('')}
                                                </div>
                                                <div>
                                                    <p className="text-gray-900 dark:text-white font-medium text-sm">{user.name}</p>
                                                    <p className="text-gray-500 dark:text-gray-400 text-xs">{user.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                                <span className={`material-icons-round text-[18px] ${user.role === 'Admin' ? 'text-purple-500' :
                                                    user.role === 'Guide' ? 'text-primary' : 'text-gray-400'
                                                    }`}>
                                                    {user.role === 'Admin' ? 'admin_panel_settings' : user.role === 'Guide' ? 'map' : 'person'}
                                                </span>
                                                <span>{user.role}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500 dark:text-gray-400">{user.location}</td>
                                        <td className="p-4">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${user.status === 'Active' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' :
                                                user.status === 'Offline' ? 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600' :
                                                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800'
                                                }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'Active' ? 'bg-green-500 animate-pulse' :
                                                    user.status === 'Offline' ? 'bg-gray-400' : 'bg-yellow-500'
                                                    }`}></span>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-gray-500 dark:text-gray-400">{user.lastActive}</td>
                                        <td className="p-4 text-right pr-6">
                                            <div className="flex items-center justify-end gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                                                <button className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-md text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors">
                                                    <span className="material-icons-round text-[20px]">edit</span>
                                                </button>
                                                <button className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md text-gray-500 hover:text-red-500 transition-colors">
                                                    <span className="material-icons-round text-[20px]">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Showing <span className="text-gray-900 dark:text-white font-bold">1-4</span> of <span className="text-gray-900 dark:text-white font-bold">48</span> users</span>
                        <div className="flex gap-2">
                            <button className="px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm">Previous</button>
                            <button className="px-3 py-1 rounded-lg bg-primary/20 border border-primary text-primary text-sm font-bold">1</button>
                            <button className="px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm">2</button>
                            <button className="px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm">3</button>
                            <button className="px-3 py-1 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600 text-sm">Next</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Inner AdminPanel Component that uses theme context
const AdminPanelInner = ({ logs, logApiCall }) => {
    const { t, language, setLanguage } = useLocalization();
    const { user, isAuthenticated, loading } = useAuth();
    const { theme } = useAdminTheme();
    const isDark = theme.isDark;
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [version, setVersion] = useState('');
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    useEffect(() => {
        fetch('/api/system/version')
            .then(res => res.json())
            .then(data => {
                if (data.version) {
                    const date = new Date(data.lastUpdated).toLocaleDateString('sv-SE');
                    setVersion(`v${data.version} (${date})`);
                }
            })
            .catch(err => console.error('Version fetch error:', err));
    }, []);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState([]);

    // Fetch notifications
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const res = await fetch('/api/bookings?limit=5');
                const data = await res.json();
                if (data.success && data.bookings) {
                    const newNotifs = data.bookings.map(b => ({
                        id: b.bookingNumber || b._id,
                        type: 'booking',
                        message: `New booking: ${b.contactDetails?.firstName || 'Guest'} (${b.totalPrice} ${b.currency})`,
                        time: new Date(b.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                        read: false
                    }));
                    setNotifications(newNotifs);
                }
            } catch (err) {
                console.error('Failed to fetch notifications', err);
            }
        };
        fetchNotifications();
        // Poll every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    // URL Sync Effect
    const location = useLocation();

    // Sync URL -> Tab
    useEffect(() => {
        // With HashRouter, location.pathname contains the path after the hash
        // e.g., "/admin/destinations"
        const currentPath = location.pathname;
        const prefix = '/admin/';

        if (currentPath.startsWith(prefix)) {
            const tabParam = currentPath.replace(prefix, '');
            // Normalize weird paths or map IDs
            if (tabParam === 'other-services') setActiveTab('other');
            else if (['dashboard', 'users', 'settings', 'ai', 'ai-planner', 'hotels', 'static-hotels', 'scraped-hotels', 'bookings', 'destinations', 'content', 'tours', 'media', 'reporting', 'pages', 'data', 'ads', 'social', 'seo', 'support', 'api', 'site', 'cache', 'apikeys', 'mapping', 'datamapper', 'publicapi', 'reisegiganten', 'email-settings'].includes(tabParam)) {
                setActiveTab(tabParam);
            }
        } else if (currentPath === '/admin' || currentPath === '/admin/') {
            setActiveTab('dashboard');
        }
    }, [location]);

    // Sync Tab -> URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        const route = tab === 'other' ? 'other-services' : tab;
        navigate(`/admin/${route}`);
    };

    // Auth guard - show login if not authenticated or not admin
    if (loading) {
        return (
            <div className="h-screen w-full bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated || user?.role !== 'admin') {
        return (
            <div className="h-screen w-full bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center p-4">
                <div className="max-w-md w-full">
                    <div className="text-center mb-8">
                        <div className="w-20 h-20 bg-teal-600/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-teal-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">Admin Access Required</h1>
                        <p className="text-gray-400">
                            {language === 'sv'
                                ? 'Endast administratörer har tillgång till denna sida.'
                                : 'Only administrators can access this page.'}
                        </p>
                    </div>

                    {isAuthenticated && user?.role !== 'admin' ? (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-center">
                            <p className="text-red-400 font-medium">
                                {language === 'sv'
                                    ? 'Ditt konto har inte administratörsbehörighet.'
                                    : 'Your account does not have admin privileges.'}
                            </p>
                            <button
                                onClick={() => navigate('/')}
                                className="mt-4 px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                {language === 'sv' ? 'Gå till startsidan' : 'Go to Home'}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <button
                                onClick={() => setShowAuthModal(true)}
                                className="w-full py-3 px-6 bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-bold rounded-xl shadow-lg hover:shadow-teal-500/30 transition-all hover:scale-[1.02]"
                            >
                                {language === 'sv' ? 'Logga in som Admin' : 'Login as Admin'}
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="w-full py-3 px-6 bg-gray-800 text-gray-300 font-medium rounded-xl border border-gray-700 hover:bg-gray-700 transition-all"
                            >
                                {language === 'sv' ? 'Gå tillbaka' : 'Go Back'}
                            </button>
                        </div>
                    )}
                </div>

                <AuthModal
                    isOpen={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                    initialMode="login"
                />
            </div>
        );
    }

    const renderSafeContent = () => {
        try {
            switch (activeTab) {
                case 'dashboard':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <DashboardContent setActiveTab={handleTabChange} version={version} />
                        </div>
                    );
                case 'users':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <UserManagement />
                        </div>
                    );
                case 'settings':
                    return (
                        <div className="flex-1 overflow-y-auto p-8">
                            <SettingsPanel onClose={() => handleTabChange('dashboard')} />
                        </div>
                    );
                case 'ai':
                    return <AIManager theme={isDark ? 'dark' : 'light'} />;
                case 'hotels':
                    return (
                        <div className={`flex-1 overflow-y-auto p-4 md:p-8 ${isDark ? 'bg-surface-dark' : 'bg-gray-50'}`}>
                            <HotelManager theme={isDark ? 'dark' : 'light'} />
                        </div>
                    );
                case 'static-hotels':
                    return (
                        <div className="flex-1 overflow-hidden">
                            <StaticHotelManager theme={isDark ? 'dark' : 'light'} />
                        </div>
                    );
                case 'scraped-hotels':
                    return (
                        <div className="flex-1 overflow-hidden">
                            <ScrapedHotelsManager theme={isDark ? 'dark' : 'light'} />
                        </div>
                    );
                case 'petra-ai':
                case 'tour-assist':
                case 'ai-planner':
                    return (
                        <div className="flex-1 overflow-hidden animate-fadeInScale">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Petra AI" />}>
                                <div className="h-full flex flex-col">
                                    {/* Petra AI Header */}
                                    <div className="bg-gradient-to-r from-rose-500/10 via-purple-500/10 to-indigo-500/10 border-b border-purple-500/20 px-6 py-3 flex items-center gap-3">
                                        <span className="material-icons-round text-purple-400 text-xl animate-pulse">auto_awesome</span>
                                        <h1 className="text-lg font-bold bg-gradient-to-r from-rose-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">Petra AI</h1>
                                        <span className="text-xs text-purple-400/60 ml-2">Intelligent Travel Assistant</span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto">
                                        <AIPlanner />
                                    </div>
                                </div>
                            </React.Suspense>
                        </div>
                    );
                case 'bookings':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <BookingsManager />
                        </div>
                    );
                case 'destinations':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <DestinationsContent currentUser={{ name: 'Admin', role: 'admin' }} theme={isDark ? 'dark' : 'light'} language={language} />
                        </div>
                    );
                case 'tours':
                    return (
                        <div className="flex-1 overflow-y-auto p-4 md:p-8">
                            <TourCacheManager />
                        </div>
                    );
                case 'media':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Media" />}>
                                <MediaContent currentUser={{ name: 'Admin', role: 'admin' }} theme={isDark ? 'dark' : 'light'} language={language} />
                            </React.Suspense>
                        </div>
                    );
                case 'reporting':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <LumiReportingContainer currentUser={{ name: 'Admin', role: 'admin' }} theme={isDark ? 'dark' : 'light'} language={language} />
                        </div>
                    );
                case 'data':
                    return (
                        <div className="flex-1 overflow-y-auto p-4 md:p-8">
                            <DataExplorer />
                        </div>
                    );
                case 'ads':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <AdManagementContent />
                        </div>
                    );
                case 'social':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <SocialMediaContent />
                        </div>
                    );
                case 'seo':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <SEOAnalyzer />
                        </div>
                    );
                case 'support':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <SupportDashboard />
                        </div>
                    );
                case 'api':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="API Management" />}>
                                <APIKeyManagement />
                            </React.Suspense>
                        </div>
                    );
                case 'site':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Site Management" />}>
                                <SiteManagement />
                            </React.Suspense>
                        </div>
                    );
                case 'cache':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Cache Management" />}>
                                <CacheManagement />
                            </React.Suspense>
                        </div>
                    );
                case 'apikeys':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="API Keys" />}>
                                <ApiKeysSettings />
                            </React.Suspense>
                        </div>
                    );
                case 'mapping':
                    return (
                        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-surface-dark">
                            <RoomMappingPanel />
                        </div>
                    );
                case 'datamapper':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Data Mapper" />}>
                                <APIDataMapper />
                            </React.Suspense>
                        </div>
                    );
                case 'publicapi':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="API Monitor" />}>
                                <ApiDiagnostics />
                            </React.Suspense>
                        </div>
                    );
                case 'other':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <OtherServicesContent />
                        </div>
                    );
                case 'api-hub':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Unified API Hub" />}>
                                <UnifiedAPIHub />
                            </React.Suspense>
                        </div>
                    );
                case 'reisegiganten':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <React.Suspense fallback={<ModuleLoadingFallback title="Reisegiganten Export" />}>
                                <ReisegigantenExport />
                            </React.Suspense>
                        </div>
                    );
                case 'flights':
                    return (
                        <div className="flex-1 flex items-center justify-center p-8">
                            <div className="text-center p-8 bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-200 dark:border-blue-900/30 max-w-md">
                                <span className="material-icons-round text-5xl text-blue-500 mb-3">flight</span>
                                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">Flyg / Flights</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Flygbiljetter ingår i paketresor. Sök paketresor för att se tillgängliga flyg.</p>
                                <button onClick={() => setActiveTab('dashboard')} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium">Tillbaka</button>
                            </div>
                        </div>
                    );
                case 'email-settings':
                    return (
                        <div className="flex-1 overflow-y-auto">
                            <EmailSettingsManager />
                        </div>
                    );
                default:
                    return (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center text-gray-500">
                                <span className="material-icons-round text-4xl mb-2">construction</span>
                                <p>Module {activeTab} coming soon...</p>
                            </div>
                        </div>
                    );
            }
        } catch (error) {
            console.error('Error rendering module:', error);
            return (
                <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center p-6 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-200 dark:border-red-900/30">
                        <span className="material-icons-round text-4xl text-red-500 mb-2">error_outline</span>
                        <h3 className="text-lg font-bold text-red-700 dark:text-red-400 mb-2">Module Error</h3>
                        <p className="text-sm text-red-600 dark:text-red-300">Failed to load the requested module.</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }
    };

    return (
        <>
            <div className="flex h-screen w-full bg-surface-light dark:bg-surface-dark transition-colors duration-300 overflow-hidden">
                <Sidebar
                    activeTab={activeTab}
                    setActiveTab={handleTabChange}
                    language={language}
                    setLanguage={setLanguage}
                    t={t}
                    isMobileOpen={isMobileSidebarOpen}
                    onCloseMobile={() => setIsMobileSidebarOpen(false)}
                />

                <main className="flex-1 flex flex-col min-w-0 bg-gray-50 dark:bg-gray-900/50 relative">
                    {/* Header with Search & User Actions */}
                    <header className="h-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700 px-6 flex items-center justify-between sticky top-0 z-30">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => setIsMobileSidebarOpen(true)}
                                className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                            >
                                <span className="material-icons-round">menu</span>
                            </button>
                            <h2 className="text-xl font-bold text-gray-800 dark:text-white capitalize hidden sm:block">
                                {t(activeTab === 'dashboard' ? 'admin_panel' : activeTab) || activeTab}
                            </h2>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="relative hidden md:block">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 material-icons-round text-lg">search</span>
                                <input
                                    type="text"
                                    placeholder={t('search_placeholder') || "Search..."}
                                    className="pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700/50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/50 w-64 transition-all"
                                />
                            </div>

                            <ThemeSelector />

                            <div className="h-8 w-[1px] bg-gray-200 dark:bg-gray-700 mx-1"></div>

                            <div className="flex items-center gap-2">
                                <button className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-xl transition-all relative">
                                    <span className="material-icons-round">notifications</span>
                                    {notifications.some(n => !n.read) && (
                                        <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-gray-800"></span>
                                    )}
                                </button>

                                {/* Notifications Dropdown */}
                                {showNotifications && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                                        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                                                <h3 className="font-bold text-gray-900 dark:text-white">Notifications</h3>
                                                <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full font-bold">
                                                    {notifications.filter(n => !n.read).length} new
                                                </span>
                                            </div>
                                            <div className="max-h-80 overflow-y-auto">
                                                {notifications.map(notif => (
                                                    <div key={notif.id} className={`px-4 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${!notif.read ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}`}>
                                                        <div className="flex items-start gap-3">
                                                            <span className={`text-lg ${notif.type === 'booking' ? '📖' : notif.type === 'system' ? '⚙️' : '⚠️'}`}>
                                                                {notif.type === 'booking' ? '📖' : notif.type === 'system' ? '⚙️' : '⚠️'}
                                                            </span>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm text-gray-900 dark:text-white truncate">{notif.message}</p>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{notif.time}</p>
                                                            </div>
                                                            {!notif.read && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                                                <button className="w-full text-center text-sm text-primary font-medium hover:underline">
                                                    View All Notifications
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>


                        </div>
                    </header>

                    {/* Content */}
                    {renderSafeContent()}
                </main>



            </div>
        </>
    );
};

// Main AdminPanel Component with Theme Provider wrapper
export const AdminDashboard = (props) => {
    return (
        <AdminThemeProvider>
            <AdminPanelInner {...props} />
        </AdminThemeProvider>
    );
};

export default AdminDashboard;